import { ftToM, parseLength, type Units } from '../units/units.ts';
import type { Run } from './types.ts';

/**
 * Photo scale (px per foot). Extracted from the original `state/project.ts`
 * (`estimateFrameFt`, verbatim), `features/editor/useScene.ts` (`useFrameWidthFt`),
 * the `setFrameWidthFt` setter clamp, and the calibration handler in
 * `features/editor/panels/LightsPanel.tsx` (`PhotoScale.calibrate`).
 */

/** Fallback guess for how many feet of house the photo spans before any run is drawn. */
export const DEFAULT_FRAME_FT = 55;
/** Typical length of the longest eave run on a house front, used to auto-scale photos. */
export const TYPICAL_LONGEST_RUN_FT = 40;
/** Range the project setter clamps a manual frame width to. */
export const MIN_FRAME_FT = 5;
export const MAX_FRAME_FT = 600;
/** Range of the "Width shown in photo" slider (calibration may land outside it). */
export const SLIDER_MIN_FRAME_FT = 15;
export const SLIDER_MAX_FRAME_FT = 200;

/**
 * Estimates the photo scale from the drawn runs until the user calibrates: the longest run is
 * assumed to be a typical ~40 ft eave. Keeps LEDs a believable size on close-ups and wide shots.
 */
export function estimateFrameFt(runs: Run[], width: number, height: number): number {
  let longest = 0;
  for (const r of runs) {
    let len = 0;
    for (let i = 1; i < r.points.length; i++) {
      len += Math.hypot((r.points[i].x - r.points[i - 1].x) * width, (r.points[i].y - r.points[i - 1].y) * height);
    }
    longest = Math.max(longest, len);
  }
  if (longest < width * 0.15) return DEFAULT_FRAME_FT;
  return Math.max(15, Math.min(200, (width * TYPICAL_LONGEST_RUN_FT) / longest));
}

/** Feet of house the photo spans: the user's calibration, else estimated from the runs (`useFrameWidthFt`). */
export function frameWidthFor(
  manual: number | null,
  runs: Run[],
  photo: { width: number; height: number } | null,
): number {
  return manual ?? (photo ? estimateFrameFt(runs, photo.width, photo.height) : DEFAULT_FRAME_FT);
}

/** The project setter's clamp (`setFrameWidthFt`): null = auto, else clamped to [5, 600]. */
export function clampFrameWidthFt(ft: number | null): number | null {
  return ft === null ? null : Math.max(MIN_FRAME_FT, Math.min(MAX_FRAME_FT, ft));
}

/** px per foot for a scene `width` px wide showing `frameWidthFt` feet. */
export function pxPerFtFor(width: number, frameWidthFt: number): number {
  return width / Math.max(1, frameWidthFt);
}

export type CalibrationResult =
  | { ok: true; enteredFt: number; frameWidthFt: number; message: string }
  | { ok: false; message: string };

export const CALIBRATION_ERROR = 'Enter the real length of the selected run in feet.';
export const CALIBRATION_ERROR_METRIC = 'Enter the real length of the selected run in meters.';

/**
 * Calibrate the photo scale from a selected run of known length (docs/spec/09).
 *
 * `entry` is parsed with `parseLength(entry, units)`: a number with an optional unit suffix
 * (ft ' in " m cm; no suffix = ft or m by `units`), so metric users can type "12.2 m" and
 * imperial users "40 ft" / "40'". `frameWidthFt` is the current effective value (manual or
 * auto); `estimatedFt` the selected run's current length in feet, or null when no run with
 * ≥ 2 points is selected.
 *
 * The returned `frameWidthFt` is the value passed to the setter (rounded to 0.1 ft, NOT yet
 * clamped; apply `clampFrameWidthFt`). The message uses the current unit:
 * `Scale set: that run is <n> ft|m` with n = the entered length in feet / metres rounded to
 * 0.01 (Math.round(v·100)/100, JavaScript number-to-string).
 */
export function calibrateFrameWidth(entry: string, frameWidthFt: number, estimatedFt: number | null, units: Units = 'imperial'): CalibrationResult {
  const entered = parseLength(entry, units);
  if (!estimatedFt || entered === null) {
    return { ok: false, message: units === 'metric' ? CALIBRATION_ERROR_METRIC : CALIBRATION_ERROR };
  }
  const shown = Math.round((units === 'metric' ? ftToM(entered) : entered) * 100) / 100;
  return {
    ok: true,
    enteredFt: entered,
    frameWidthFt: Math.round(frameWidthFt * (estimatedFt / entered) * 10) / 10,
    message: `Scale set: that run is ${shown} ${units === 'metric' ? 'm' : 'ft'}`,
  };
}

/**
 * ORIGINAL calibration (the Lights panel handler): entered = parseFloat(text with the first ","
 * replaced by "."), JavaScript prefix parsing ("40 ft" → 40, "1,2,5" → 1.2), feet only.
 */
export function calibrateFrameWidthOriginal(entry: string, frameWidthFt: number, estimatedFt: number | null): CalibrationResult {
  const entered = parseFloat(entry.replace(',', '.'));
  if (!estimatedFt || !Number.isFinite(entered) || entered <= 0) {
    return { ok: false, message: CALIBRATION_ERROR };
  }
  return {
    ok: true,
    enteredFt: entered,
    frameWidthFt: Math.round(frameWidthFt * (estimatedFt / entered) * 10) / 10,
    message: `Scale set: that run is ${entered} ft`,
  };
}
